import React from "react";
import {
  Landmark,
  Home,
  Castle,
  Skull,
  Church,
  Building2,
  Ship,
  Tent,
  Shield,
  Building,
  Store,
  Bone,
  TriangleAlert,
  MapPin,
} from "lucide-react";
import { LOCATION_CATEGORIES } from "./api";

const ICON_MAP = {
  Landmark,
  Home,
  Castle,
  Skull,
  Church,
  Building2,
  Ship,
  Tent,
  Shield,
  Building,
  Store,
  Bone,
  TriangleAlert,
  MapPin,
};

export function getCategory(id) {
  return LOCATION_CATEGORIES.find((c) => c.id === id) || LOCATION_CATEGORIES[0];
}

export function CategoryIcon({ id, className = "w-4 h-4", style }) {
  const cat = getCategory(id);
  const Icon = ICON_MAP[cat.icon] || MapPin;
  return <Icon className={className} style={{ color: cat.color, ...(style || {}) }} />;
}

/**
 * A fantasy pin rendered as an SVG group.
 * Renders a shield/banner style crest with the category icon.
 *
 * Props:
 * - x, y (screen coords in svg viewbox)
 * - size (px)
 * - category (id)
 * - selected, highlighted
 */
export function FantasyPin({ x, y, size = 28, category, selected, highlighted, style = "shield" }) {
  const cat = getCategory(category);
  const s = size;

  // The pin uses a shield or banner shape as background + a lucide icon in center.
  const glow = selected
    ? "drop-shadow(0 0 8px rgba(212,175,55,0.95))"
    : highlighted
    ? "drop-shadow(0 0 6px rgba(212,175,55,0.6))"
    : "drop-shadow(0 2px 2px rgba(0,0,0,0.7))";

  return (
    <g style={{ filter: glow }}>
      {/* Base shadow */}
      <ellipse cx={x} cy={y + s * 0.55} rx={s * 0.35} ry={s * 0.1} fill="rgba(0,0,0,0.45)" />

      {/* Shield/Banner */}
      {style === "banner" ? (
        <BannerShape x={x} y={y} size={s} color={cat.color} selected={selected} />
      ) : style === "seal" ? (
        <SealShape x={x} y={y} size={s} color={cat.color} selected={selected} />
      ) : (
        <ShieldShape x={x} y={y} size={s} color={cat.color} selected={selected} />
      )}
    </g>
  );
}

function ShieldShape({ x, y, size, color, selected }) {
  const w = size;
  const h = size * 1.15;
  const cx = x;
  const cy = y;
  // Heraldic shield path centered on (cx, cy)
  const half = w / 2;
  const top = cy - h / 2;
  const bot = cy + h / 2;
  const d = `
    M ${cx - half} ${top + 2}
    L ${cx + half} ${top + 2}
    L ${cx + half} ${top + h * 0.55}
    Q ${cx + half} ${bot} ${cx} ${bot}
    Q ${cx - half} ${bot} ${cx - half} ${top + h * 0.55}
    Z
  `;
  return (
    <>
      {/* Outer gold band */}
      <path d={d} fill="#d4af37" stroke="#000" strokeWidth="1" />
      {/* Inner colored field */}
      <path
        d={d}
        fill={color}
        transform={`translate(${cx} ${cy}) scale(0.82) translate(${-cx} ${-cy})`}
        stroke={selected ? "#fef3c7" : "rgba(0,0,0,0.6)"}
        strokeWidth={selected ? "1.5" : "0.8"}
      />
      {/* Highlight */}
      <path
        d={`M ${cx - half * 0.7} ${top + 6} Q ${cx - half * 0.7} ${cy - h * 0.15} ${cx - half * 0.1} ${cy - h * 0.15}`}
        stroke="rgba(255,255,255,0.35)"
        strokeWidth="1.2"
        fill="none"
      />
    </>
  );
}

function BannerShape({ x, y, size, color, selected }) {
  const w = size;
  const h = size * 1.3;
  const cx = x;
  const cy = y;
  const half = w / 2;
  const top = cy - h / 2;
  const bot = cy + h / 2;
  const d = `
    M ${cx - half} ${top}
    L ${cx + half} ${top}
    L ${cx + half} ${bot - h * 0.15}
    L ${cx} ${bot}
    L ${cx - half} ${bot - h * 0.15}
    Z
  `;
  return (
    <>
      <path d={d} fill="#d4af37" stroke="#000" strokeWidth="1" />
      <path
        d={d}
        fill={color}
        transform={`translate(${cx} ${cy}) scale(0.85) translate(${-cx} ${-cy})`}
        stroke={selected ? "#fef3c7" : "rgba(0,0,0,0.5)"}
        strokeWidth={selected ? "1.2" : "0.5"}
      />
    </>
  );
}

function SealShape({ x, y, size, color, selected }) {
  const r = size * 0.55;
  return (
    <>
      <circle cx={x} cy={y} r={r} fill="#d4af37" stroke="#000" strokeWidth="1" />
      <circle
        cx={x}
        cy={y}
        r={r * 0.82}
        fill={color}
        stroke={selected ? "#fef3c7" : "rgba(0,0,0,0.6)"}
        strokeWidth={selected ? "1.5" : "0.8"}
      />
      {/* Notches */}
      {Array.from({ length: 8 }).map((_, i) => {
        const a = (i / 8) * 2 * Math.PI;
        const x1 = x + r * Math.cos(a);
        const y1 = y + r * Math.sin(a);
        const x2 = x + (r + 3) * Math.cos(a);
        const y2 = y + (r + 3) * Math.sin(a);
        return <line key={i} x1={x1} y1={y1} x2={x2} y2={y2} stroke="#d4af37" strokeWidth="1.5" />;
      })}
    </>
  );
}
